import { useEffect, useMemo, useState } from 'react';
import { Boxes, Check, Save, Sparkles } from 'lucide-react';
import { readToken, superadminTokenKey } from '../../app/session';
import { ApiError, apiRequest } from '../../lib/api';
import type { Company, Module } from './types';

export function ModulesPage() {
  const token = readToken(superadminTokenKey);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function load(preferredCompanyId?: string) {
    try {
      const [companyResponse, moduleResponse] = await Promise.all([
        apiRequest<{ items: Company[] }>('/api/v1/superadmin/companies', {
          accessToken: token
        }),
        apiRequest<{ items: Module[] }>('/api/v1/superadmin/companies/modules', {
          accessToken: token
        })
      ]);
      setCompanies(companyResponse.items);
      setModules(moduleResponse.items);
      const nextCompanyId =
        preferredCompanyId || companyId || companyResponse.items[0]?.id || '';
      setCompanyId(nextCompanyId);
      setSelected(
        companyResponse.items.find((company) => company.id === nextCompanyId)
          ?.enabledModules ?? []
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Moduli nisu dostupni.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const company = useMemo(
    () => companies.find((item) => item.id === companyId),
    [companies, companyId]
  );

  function selectCompany(id: string) {
    setCompanyId(id);
    setSelected(companies.find((item) => item.id === id)?.enabledModules ?? []);
    setMessage('');
  }

  function toggle(module: Module) {
    if (module.isCore || module.status !== 'ACTIVE') return;
    setSelected((current) =>
      current.includes(module.key)
        ? current.filter((key) => key !== module.key)
        : [...current, module.key]
    );
  }

  async function save() {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await apiRequest<{ enabledModules: string[] }>(
        `/api/v1/superadmin/companies/${companyId}/modules`,
        {
          method: 'PATCH',
          accessToken: token,
          body: JSON.stringify({ moduleKeys: selected })
        }
      );
      setSelected(response.enabledModules);
      await load(companyId);
      setMessage('Paket modula je odmah aktiviran.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Moduli nisu spremljeni.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <span className="eyebrow">Paketi bez promjene koda</span>
          <h1>Moduli</h1>
          <p>Odaberi firmu, klikni mogućnosti i spremi. Ovisnosti se rješavaju same.</p>
        </div>
      </header>

      <section className="module-catalog">
        {modules.map((module) => (
          <article key={module.id}>
            <span className={`catalog-icon ${module.status !== 'ACTIVE' ? 'is-muted' : ''}`}><Boxes /></span>
            <div><strong>{module.name}</strong><small>{module.description}</small></div>
            <span className="usage-count">{module.enabledCompanyCount} firmi</span>
          </article>
        ))}
      </section>

      <section className="panel module-manager">
        <div className="panel__toolbar">
          <div><h2>Uredi paket firme</h2><p className="muted">Promjena vrijedi odmah i na backend rutama.</p></div>
          <select value={companyId} onChange={(event) => selectCompany(event.target.value)}>
            {companies.map((item) => <option value={item.id} key={item.id}>{item.displayName}</option>)}
          </select>
        </div>
        {!companies.length ? (
          <div className="table-empty">Prvo otvori barem jednu firmu.</div>
        ) : (
          <div className="module-manager__body">
            <div className="selected-company">
              <span style={{ background: company?.primaryColor }}>{company?.displayName.slice(0, 2).toUpperCase()}</span>
              <div><strong>{company?.displayName}</strong><small>{selected.length} uključenih modula</small></div>
            </div>
            <div className="module-switch-grid">
              {modules.map((module) => {
                const active = selected.includes(module.key) || module.isCore;
                return (
                  <button
                    key={module.id}
                    className={`${active ? 'is-active' : ''} ${module.status !== 'ACTIVE' ? 'is-disabled' : ''}`}
                    onClick={() => toggle(module)}
                  >
                    <span>{active ? <Check /> : <Boxes />}</span>
                    <div><strong>{module.name}</strong><small>{module.isCore ? 'Uvijek uključeno' : module.dependencies.length ? `Treba: ${module.dependencies.join(', ')}` : 'Samostalan modul'}</small></div>
                  </button>
                );
              })}
            </div>
            <div className="manager-footer">
              <p><Sparkles /> Ako modul treba drugi modul, sustav će ga uključiti automatski.</p>
              <button className="button button--primary" onClick={() => void save()} disabled={busy}><Save /> {busy ? 'Spremam…' : 'Spremi paket'}</button>
            </div>
            {message && <div className="form-success">{message}</div>}
            {error && <div className="form-error">{error}</div>}
          </div>
        )}
      </section>
    </>
  );
}
